'use client';

import { useRef, useEffect, useState } from 'react';
import { HOMEPAGE_V2_FLAG, isSe3EnabledParam } from 'utils/bootstrapHelpers';
import type { HomepageVariant } from 'utils/bootstrapHelpers';
import { trackEvent } from 'utils/analytics';
import { STORAGE_KEYS } from 'utils/constants/localStorage';

interface UseHomepageVariantOptions {
  serverVariant?: HomepageVariant;
  pageName?: string;
}

interface UseHomepageVariantReturn {
  variant: HomepageVariant;
  isV2: boolean;
  isResolved: boolean;
}

export const useHomepageVariant = ({
  serverVariant: _serverVariant = 'control',
  pageName: _pageName = 'unknown',
}: UseHomepageVariantOptions = {}): UseHomepageVariantReturn => {
  const hasTracked = useRef(false);
  const isResolved = true;

  // Priority 0: URL param override (?v2=true or ?v2=false)
  // Persists to sessionStorage so it carries across page navigations
  const [urlParamVariant] = useState<HomepageVariant | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    if (isSe3EnabledParam(params.get('se3'))) {
      try {
        sessionStorage.removeItem(STORAGE_KEYS.HOMEPAGE_V2_OVERRIDE);
      } catch {}
      return 'control';
    }
    const v2Param = params.get('v2');
    if (v2Param === 'true') {
      try {
        sessionStorage.setItem(STORAGE_KEYS.HOMEPAGE_V2_OVERRIDE, 'v2');
      } catch {}
      return 'v2';
    }
    if (v2Param === 'false') {
      try {
        sessionStorage.removeItem(STORAGE_KEYS.HOMEPAGE_V2_OVERRIDE);
      } catch {}
      return 'control';
    }
    try {
      const stored = sessionStorage.getItem(STORAGE_KEYS.HOMEPAGE_V2_OVERRIDE);
      if (stored === 'v2') return 'v2';
    } catch {}
    return null;
  });

  // Session-sticky V2: once variant resolves to 'v2' from any source during this
  // session, persist it so the PostHog SDK can't downgrade to 'control' after
  // identity changes (e.g. login) or async re-evaluation.
  const [stickyV2, setStickyV2] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem(STORAGE_KEYS.HOMEPAGE_V2_OVERRIDE) === 'v2';
    } catch {
      return false;
    }
  });

  // Server-side prop is the fallback for legacy pages reached through an override.
  // urlParamVariant='control' explicitly overrides everything (user used ?v2=false)
  const variant: HomepageVariant =
    urlParamVariant === 'control'
      ? 'control'
      : urlParamVariant || (stickyV2 ? 'v2' : null) || _serverVariant;

  // Persist V2 assignment to sessionStorage so it survives PostHog SDK re-evaluation
  useEffect(() => {
    if (variant === 'v2' && !stickyV2) {
      setStickyV2(true);
      try {
        sessionStorage.setItem(STORAGE_KEYS.HOMEPAGE_V2_OVERRIDE, 'v2');
      } catch {}
    }
  }, [variant, stickyV2]);

  useEffect(() => {
    if (!isResolved || hasTracked.current) return;
    hasTracked.current = true;

    let source = 'server_prop';
    if (urlParamVariant) source = 'url_param';

    trackEvent('ab_test_assigned', {
      experiment: HOMEPAGE_V2_FLAG,
      variant,
      source,
      page: _pageName,
    });

    // Single batched replaceState: clean ?v2 param + keep se_experiment aligned.
    if (typeof window !== 'undefined') {
      try {
        const href = window.location.href;
        if (href && href !== 'about:blank') {
          const url = new URL(href);
          let changed = false;

          // Clean up ?v2 param if present
          if (url.searchParams.has('v2')) {
            url.searchParams.delete('v2');
            changed = true;
          }

          // Inject se_experiment param for V2 variant
          if (variant === 'v2') {
            const value = `${HOMEPAGE_V2_FLAG}_v2`;
            if (url.searchParams.get('se_experiment') !== value) {
              url.searchParams.set('se_experiment', value);
              changed = true;
            }
          } else if (url.searchParams.has('se_experiment')) {
            url.searchParams.delete('se_experiment');
            changed = true;
          }

          if (changed) {
            window.history.replaceState(window.history.state, '', url.toString());
          }
        }
      } catch {}
    }
  }, [isResolved, variant, _pageName, urlParamVariant]);

  return { variant, isV2: variant === 'v2', isResolved };
};

export default useHomepageVariant;
